import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Plus, Save, FileText, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function AdminPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newDoc, setNewDoc] = useState({ id: "", name: "", description: "", filename: "" });

  const fetchDocs = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/documents");
      setDocs(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Accès refusé : réservé à l'admin.");
        nav("/dashboard");
      } else {
        toast.error("Impossible de charger les documents.");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    document.title = "Admin · AURUM Academy";
    if (!authLoading && !user) { nav("/login"); return; }
    if (!authLoading && user) fetchDocs();
  }, [authLoading, user, fetchDocs, nav]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!newDoc.id || !newDoc.name) {
      toast.error("ID et nom sont obligatoires.");
      return;
    }
    try {
      await api.post("/admin/documents", newDoc);
      toast.success("Document créé.");
      setNewDoc({ id: "", name: "", description: "", filename: "" });
      setCreating(false);
      fetchDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de création");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(`Supprimer "${id}" définitivement ?`)) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      toast.success("Supprimé.");
      fetchDocs();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#A1A1AA]">
        <Loader2 className="h-5 w-5 animate-spin mr-3" />
        <span className="text-xs uppercase tracking-[0.3em]">Chargement…</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#0A0A0A]">
      <header className="border-b border-white/10 bg-[#0c0c0c]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
            <span className="text-[#52525B]">/</span>
            <span className="font-serif text-white">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#A1A1AA]">{user?.code_label}</span>
            <button onClick={onLogout} className="px-3 py-1.5 border border-white/15 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="admin-logout">
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Panneau admin</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light mt-3 leading-tight">Gestion des documents</h1>
            <p className="mt-4 text-sm text-[#A1A1AA] max-w-2xl">
              Ajoutez, remplacez, renommez ou supprimez les documents téléchargeables. Les modifications sont
              visibles immédiatement par tous les apprenants.
            </p>
          </div>
          <button
            onClick={() => setCreating(!creating)}
            data-testid="admin-toggle-create"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.22em] hover:bg-[#F5F5DC] transition-colors"
          >
            <Plus className="h-4 w-4" /> {creating ? "Annuler" : "Nouveau document"}
          </button>
        </div>

        {creating && (
          <form onSubmit={onCreate} data-testid="admin-create-form" className="mb-10 border border-[#D4AF37]/40 bg-[#141414] p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="ID unique (ex: doc-nda, doc-spa-ciment)" required value={newDoc.id} onChange={(v) => setNewDoc({ ...newDoc, id: v })} placeholder="doc-xxx" testid="admin-new-id" />
            <FormField label="Nom affiché" required value={newDoc.name} onChange={(v) => setNewDoc({ ...newDoc, name: v })} placeholder="NDA — Or" testid="admin-new-name" />
            <FormField label="Nom de fichier (optionnel)" value={newDoc.filename} onChange={(v) => setNewDoc({ ...newDoc, filename: v })} placeholder="AURUM_NDA.pdf" testid="admin-new-filename" />
            <FormField label="Description" value={newDoc.description} onChange={(v) => setNewDoc({ ...newDoc, description: v })} placeholder="Courte description du document…" testid="admin-new-description" full />
            <div className="md:col-span-2">
              <button type="submit" data-testid="admin-submit-create" className="inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.22em] hover:bg-[#F5F5DC]">
                <Plus className="h-4 w-4" /> Créer le document
              </button>
              <p className="mt-3 text-xs text-[#A1A1AA]">Après création, uploadez le fichier via le bouton « Upload » sur la ligne correspondante.</p>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {docs.map((d) => (
            <AdminDocRow key={d.id} doc={d} onChange={fetchDocs} onDelete={() => onDelete(d.id)} />
          ))}
          {docs.length === 0 && (
            <div className="text-center text-[#A1A1AA] py-16">Aucun document. Créez-en un ci-dessus.</div>
          )}
        </div>
      </main>
    </div>
  );
}

const FormField = ({ label, value, onChange, placeholder, required, full, testid }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA]">{label}{required && <span className="text-[#D4AF37]"> *</span>}</span>
    <input
      data-testid={testid}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-2 w-full bg-black/50 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]"
    />
  </label>
);

const AdminDocRow = ({ doc, onChange, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(doc.name);
  const [description, setDescription] = useState(doc.description);
  const [filename, setFilename] = useState(doc.filename);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const save = async () => {
    try {
      await api.patch(`/admin/documents/${doc.id}`, { name, description, filename });
      toast.success("Modifications enregistrées.");
      setEditing(false);
      onChange();
    } catch {
      toast.error("Erreur de sauvegarde");
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/admin/documents/${doc.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${file.name}" uploadé.`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={`admin-doc-row-${doc.id}`} className={`border ${doc.has_real_file ? "border-white/10" : "border-[#D4AF37]/30"} bg-[#0c0c0c] p-5`}>
      <div className="flex items-start gap-4">
        <div className={`h-10 w-10 flex items-center justify-center border ${doc.has_real_file ? "border-[#34C759]/40 text-[#34C759]" : "border-[#D4AF37]/40 text-[#D4AF37]"}`}>
          {doc.has_real_file ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Nom affiché" value={name} onChange={setName} />
              <FormField label="Nom de fichier" value={filename} onChange={setFilename} />
              <FormField label="Description" value={description} onChange={setDescription} full />
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">{doc.id}</span>
                <span className="font-serif text-lg text-white">{doc.name}</span>
                {!doc.has_real_file && <span className="text-[9px] uppercase tracking-[0.28em] text-[#D4AF37]">Placeholder</span>}
              </div>
              <p className="text-sm text-[#A1A1AA] mt-2">{doc.description}</p>
              <p className="text-xs text-[#52525B] mt-2 font-mono">{doc.filename}{doc.file ? ` · ${doc.file}` : " · (aucun fichier)"}</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
          {editing ? (
            <>
              <button onClick={save} data-testid={`admin-save-${doc.id}`} className="inline-flex items-center gap-2 px-3 py-2 bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.22em]">
                <Save className="h-3.5 w-3.5" /> Enregistrer
              </button>
              <button onClick={() => { setEditing(false); setName(doc.name); setDescription(doc.description); setFilename(doc.filename); }} className="px-3 py-2 border border-white/15 text-[10px] uppercase tracking-[0.22em]">
                Annuler
              </button>
            </>
          ) : (
            <>
              <input ref={fileInput} type="file" className="hidden" data-testid={`admin-file-input-${doc.id}`} onChange={(e) => upload(e.target.files?.[0])} accept=".pdf,.png,.jpg,.jpeg,.webp,.avif,.gif" />
              <button onClick={() => fileInput.current?.click()} disabled={uploading} data-testid={`admin-upload-${doc.id}`} className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-[10px] uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {uploading ? "Upload…" : doc.has_real_file ? "Remplacer" : "Upload"}
              </button>
              <button onClick={() => setEditing(true)} data-testid={`admin-edit-${doc.id}`} className="px-3 py-2 border border-white/15 text-[10px] uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37]">
                Modifier
              </button>
              <button onClick={onDelete} data-testid={`admin-delete-${doc.id}`} className="inline-flex items-center gap-2 px-3 py-2 border border-[#FF3B30]/30 text-[#FF3B30] text-[10px] uppercase tracking-[0.22em] hover:bg-[#FF3B30]/10">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
